import _ from 'lodash'
import { TransformData, UnitSize, Z_LAYER_KEYS, Z_LAYER_VALUE_MAP } from '@wix/thunderbolt-becky-types'

const orderedTransformationKeys = ['translate', 'scale', 'rotate', 'skew']
const DEFAULT_TRANSLATE = 'translateX(0)translateY(0)'
const DEFAULT_SCALE = 'scaleX(1)scaleY(1)'
const DEFAULT_SKEW = 'skewX(0deg)skewY(0deg)'
const DEFAULT_ROTATE = 'rotate(0deg)'

const isPropertyDefined = (property: keyof TransformData, styleItem: Partial<TransformData>) =>
	styleItem.hasOwnProperty(property) && styleItem[property] !== undefined && styleItem[property] !== null

const omitUndefinedProperties = (styleItem: TransformData): Partial<TransformData> =>
	_.pickBy(styleItem, (__, property) => isPropertyDefined(property as keyof TransformData, styleItem))

const omitIgnoredProperties = (
	styleItem: Partial<TransformData>,
	ignoredProperties: Array<string>
): Partial<TransformData> => _.omitBy(styleItem, (__, property) => ignoredProperties.includes(property))

const getUnit = (unit: UnitSize['type']) => {
	if (unit === 'percentage') {
		return '%'
	}
	return unit
}

const resolveValueWithUnit = (valueWithUnit?: UnitSize) =>
	valueWithUnit ? `${valueWithUnit.value}${getUnit(valueWithUnit.type)}` : 0

const resolveNumber = (value: number) => value || 0

const isStyleItemContainsTransformProps = (styleItem: Partial<TransformData>) =>
	orderedTransformationKeys.some((property) => isPropertyDefined(property as keyof TransformData, styleItem))

const getTranslateString = (translate?: TransformData['translate']) => {
	let translateString = ''
	if (!translate) {
		return DEFAULT_TRANSLATE
	}
	const { x: translateX, y: translateY } = translate
	if (translateX) {
		translateString += `translateX(${resolveValueWithUnit(translateX)})`
	} else {
		translateString += 'translateX(0)'
	}
	if (translateY) {
		translateString += `translateY(${resolveValueWithUnit(translateY)})`
	} else {
		translateString += 'translateY(0)'
	}
	return translateString
}

const getScaleString = (scale?: TransformData['scale']) => {
	let scaleString = ''
	if (!scale) {
		return DEFAULT_SCALE
	}
	const { x: scaleX = 1, y: scaleY = 1 } = scale
	if (_.isNumber(scaleX)) {
		scaleString += `scaleX(${scaleX})`
	}
	if (_.isNumber(scaleY)) {
		scaleString += `scaleY(${scaleY})`
	}
	return scaleString
}

const getRotateString = (componentDefaultRotation?: number, rotate?: number) => {
	if (_.isNumber(rotate) || componentDefaultRotation) {
		const compRotation = componentDefaultRotation || 0
		const variantRotation = rotate || 0
		// chrome bug - An element with 3d transform ignores 'overflow: hidden' on a parent, when angles are multiplies of 90.
		// Jira - https://jira.wixpress.com/browse/WOW-83
		// Chrome bug report - https://bugs.chromium.org/p/chromium/issues/detail?id=1207151
		const adjustRotationForChrome = rotate && rotate % 90 === 0 ? 0.0001 : 0
		return `rotate(${resolveNumber(variantRotation) + compRotation + adjustRotationForChrome}deg)`
	}
	return DEFAULT_ROTATE
}

const getSkewString = (skew?: TransformData['skew']) => {
	let skewString = ''
	if (!skew) {
		return DEFAULT_SKEW
	}
	const { x: skewX = 0, y: skewY = 0 } = skew
	if (_.isNumber(skewX)) {
		skewString += `skewX(${skewX}deg)`
	}
	if (_.isNumber(skewY)) {
		skewString += `skewY(${skewY}deg)`
	}
	return skewString
}

const getCSSFromTransformationItem = (
	styleItem: TransformData,
	ignoredTransformProps: Array<string>,
	componentDefaultRotation?: number
) => {
	const css = []
	const filteredStyleItem = omitIgnoredProperties(omitUndefinedProperties(styleItem), ignoredTransformProps)
	if (Z_LAYER_VALUE_MAP[filteredStyleItem.zLayer as Z_LAYER_KEYS]) {
		css.push(`z-index: ${Z_LAYER_VALUE_MAP[filteredStyleItem.zLayer as Z_LAYER_KEYS]}`)
	}
	if (filteredStyleItem.hasOwnProperty('hidden')) {
		css.push(`opacity: ${filteredStyleItem.hidden ? 0 : 1}`)
		css.push(`visibility: ${filteredStyleItem.hidden ? 'hidden' : 'visible'}`)
	} else if (filteredStyleItem.hasOwnProperty('opacity')) {
		css.push(`opacity: ${filteredStyleItem.opacity}`)
	}
	if (filteredStyleItem.origin) {
		css.push(
			`transform-origin: ${resolveValueWithUnit(filteredStyleItem.origin.x)} ${resolveValueWithUnit(
				filteredStyleItem.origin.y
			)}`
		)
	}
	if (filteredStyleItem.filter) {
		const { blur = 0, opacity = 100, grayscale = 0 } = filteredStyleItem.filter
		css.push(`filter: opacity(${opacity}%) grayscale(${grayscale}%) blur(${blur}px)`)
	}
	if (isStyleItemContainsTransformProps(filteredStyleItem)) {
		const { scale, rotate, translate, skew } = filteredStyleItem

		const transformationsString = {
			translate: getTranslateString(translate),
			scale: getScaleString(scale),
			rotate: getRotateString(componentDefaultRotation, rotate),
			skew: getSkewString(skew),
		}

		const transformStringValue = orderedTransformationKeys
			.reduce(
				(transformString, transformKey) =>
					`${transformString}${
						transformationsString[transformKey as 'translate' | 'scale' | 'rotate' | 'skew']
					}`,
				''
			)
			.trim()
		if (transformStringValue) {
			const transformString = `transform: ${transformStringValue}`
			css.push(transformString)
		}
	}

	return css.length ? `${css.join('; ')}` : ''
}

export { getCSSFromTransformationItem }
